import { gql } from '@apollo/client'

const REGISTER_ATTRIBUTES = gql`
  fragment registerAttributes on Register {
    id
    createdAt
    archivedAt
    typeLibraryTemplateId

    name
    identifierPrefix
    settings {
      canAssignItemsToProject
      canAssignMultipleProjects
      canAssignChecklists
      canSubcontractorsManageItems

      hideAssignees
      hideFollowUpDate
      hideIdentifier

      checklistColumns {
        templateId
        blockId
        title
      }
    }

    template {
      id
      templateData
    }

    typeLibraryTemplate {
      id
      templateName
      templateData
    }

    projects {
      id
      name
      status
    }
  }
`

const REGISTER_LIST_ATTRIBUTES = gql`
  fragment registerListAttributes on Register {
    id
    createdAt
    archivedAt

    name
    identifierPrefix

    projects {
      name
      id
      status
    }

    typeLibraryTemplate {
      templateName
    }

    itemCount
  }
`

export const GET_REGISTERS_LIST = gql`
  query registers_UI($filter: RegisterFilterInput) {
    registers(filter: $filter) {
      ...registerListAttributes
    }
  }
  ${REGISTER_LIST_ATTRIBUTES}
`

export const GET_REGISTER_IDENTIFIERS = gql`
  query registerIdentifiers_UI {
    registerIdentifiers
  }
`

export const GET_REGISTER = gql`
  query register_UI($identifierPrefix: String!, $filter: RegisterFilterInput) {
    register(identifierPrefix: $identifierPrefix, filter: $filter) {
      ...registerAttributes
    }
  }
  ${REGISTER_ATTRIBUTES}
`

export const CREATE_REGISTER = gql`
  mutation createRegister_UI($input: CreateRegisterInput!) {
    createRegister(input: $input) {
      ...registerAttributes
    }
  }
  ${REGISTER_ATTRIBUTES}
`

export const UPDATE_REGISTER = gql`
  mutation updateRegister_UI($id: String!, $input: UpdateRegisterInput!) {
    updateRegister(id: $id, input: $input) {
      ...registerAttributes
    }
  }
  ${REGISTER_ATTRIBUTES}
`

export const ARCHIVE_REGISTER = gql`
  mutation archiveRegister_UI($id: String!) {
    archiveRegister(id: $id) {
      ...registerAttributes
    }
  }
  ${REGISTER_ATTRIBUTES}
`

export const RESTORE_REGISTER = gql`
  mutation restoreRegister_UI($id: String!) {
    restoreRegister(id: $id) {
      ...registerAttributes
    }
  }
  ${REGISTER_ATTRIBUTES}
`

const REGISTER_ITEM_ATTRIBUTES = gql`
  fragment registerItemAttributes on RegisterItem {
    id
    createdAt
    updatedAt
    archivedAt
    followUpAt
    status
    identifier
    fullIdentifier

    name

    projects {
      id
      name
      status
    }

    bookings {
      id
      title
      startDate
      endDate
    }

    registerItemType {
      id
      name
    }

    checklistTemplates {
      id
      templateName
      templateData # for rendering the promoted columns
    }

    checklistTemplateResponses {
      id
    }

    assignees {
      id
      type
      worker {
        id
        trade
        user {
          id
          firstName
          lastName
        }
        subcontractors {
          id
          company {
            name
          }
        }
      }
      adminUser {
        id
        user {
          id
          firstName
          lastName
        }
      }
      subcontractor {
        id
        company {
          name
        }
        projects {
          name
        }
      }
    }

    templateResponse {
      id
      updatedAt
      responseData
    }

    typeTemplateResponse {
      id
      updatedAt
      responseData
    }

    register {
      identifierPrefix
      typeLibraryTemplateId
      typeLibraryTemplate {
        templateName
      }

      template {
        templateData
      }
      settings {
        canAssignItemsToProject
        canAssignMultipleProjects
        canAssignChecklists
        canSubcontractorsManageItems

        hideAssignees
        hideFollowUpDate
        hideIdentifier
      }
    }
  }
`

export const GET_REGISTER_ITEM = gql`
  query registerItem_UI($identifierPrefix: String!, $identifier: Int!) {
    registerItem(identifierPrefix: $identifierPrefix, identifier: $identifier) {
      ...registerItemAttributes

      uploadTemplateResponse {
        id
        responseData
        updatedAt
      }

      reviewTemplateResponse {
        id
        responseData
        updatedAt
      }

      registerItemType {
        id
        reviewTemplate {
          id
          templateData
        }
      }
    }
  }
  ${REGISTER_ITEM_ATTRIBUTES}
`

export const CREATE_REGISTER_ITEM = gql`
  mutation createRegisterItem_UI($input: CreateRegisterItemInput!) {
    createRegisterItem(input: $input) {
      ...registerItemAttributes
    }
  }
  ${REGISTER_ITEM_ATTRIBUTES}
`

export const UPDATE_REGISTER_ITEM = gql`
  mutation updateRegisterItem_UI(
    $id: String!
    $input: UpdateRegisterItemInput!
  ) {
    updateRegisterItem(id: $id, input: $input) {
      ...registerItemAttributes
    }
  }
  ${REGISTER_ITEM_ATTRIBUTES}
`

export const ARCHIVE_REGISTER_ITEM = gql`
  mutation archiveRegisterItem_UI($id: String!) {
    archiveRegisterItem(id: $id) {
      ...registerItemAttributes
    }
  }
  ${REGISTER_ITEM_ATTRIBUTES}
`

export const RESTORE_REGISTER_ITEM = gql`
  mutation restoreRegisterItem_UI($id: String!) {
    restoreRegisterItem(id: $id) {
      ...registerItemAttributes
    }
  }
  ${REGISTER_ITEM_ATTRIBUTES}
`
export const SUBMIT_REGISTER_ITEM_REVIEW = gql`
  mutation submitRegisterItemReview_UI(
    $id: String!
    $input: SubmitRegisterItemReviewInput!
  ) {
    submitRegisterItemReview(id: $id, input: $input) {
      ...registerItemAttributes
    }
  }
  ${REGISTER_ITEM_ATTRIBUTES}
`

const REGISTER_ITEM_BOOKING_ATTRIBUTES = gql`
  fragment registerItemBookingAttributes on RegisterItemBooking {
    id
    createdAt
    updatedAt
    archivedAt

    startDate
    endDate

    title

    registerItem {
      id
      name
      fullIdentifier
    }
  }
`

export const GET_REGISTER_ITEM_BOOKINGS = gql`
  query registerItemBookings_UI($filter: RegisterItemBookingFilterInput!) {
    registerItemBookings(filter: $filter) {
      ...registerItemBookingAttributes
    }
  }
  ${REGISTER_ITEM_BOOKING_ATTRIBUTES}
`

export const CREATE_REGISTER_ITEM_BOOKING = gql`
  mutation createRegisterItemBooking_UI(
    $input: CreateRegisterItemBookingInput!
  ) {
    createRegisterItemBooking(input: $input) {
      ...registerItemBookingAttributes
    }
  }
  ${REGISTER_ITEM_BOOKING_ATTRIBUTES}
`

export const UPDATE_REGISTER_ITEM_BOOKING = gql`
  mutation updateRegisterItemBooking_UI(
    $id: String!
    $input: UpdateRegisterItemBookingInput!
  ) {
    updateRegisterItemBooking(id: $id, input: $input) {
      ...registerItemBookingAttributes
    }
  }
  ${REGISTER_ITEM_BOOKING_ATTRIBUTES}
`

export const ARCHIVE_REGISTER_ITEM_BOOKING = gql`
  mutation archiveRegisterItemBooking_UI($id: String!) {
    archiveRegisterItemBooking(id: $id) {
      ...registerItemBookingAttributes
    }
  }
  ${REGISTER_ITEM_BOOKING_ATTRIBUTES}
`

export const RESTORE_REGISTER_ITEM_BOOKING = gql`
  mutation restoreRegisterItemBooking_UI($id: String!) {
    restoreRegisterItemBooking(id: $id) {
      ...registerItemBookingAttributes
    }
  }
  ${REGISTER_ITEM_BOOKING_ATTRIBUTES}
`

export const REGISTER_ITEMS_FOR_SEARCH = gql`
  query registerItemsForSearch_UI(
    $filter: RegisterItemFilterInput
    $pagination: PaginationInput
  ) {
    registerItems(filter: $filter, pagination: $pagination) {
      id
      identifier
      name

      register {
        id
        name
        identifierPrefix
      }

      projects {
        id
        name
        status
      }
    }
  }
`

export const GET_WORKER_REGISTERS = gql`
  query workerRegisters_UI($filter: RegistersWorkerFilterInput) {
    registers_worker(filter: $filter) {
      id
      name
      identifierPrefix
      builder {
        id
        company {
          name
        }
      }
    }
  }
`

export const GET_WORKER_REGISTER = gql`
  query workerRegister_UI($registerId: String!, $projectId: String) {
    register_worker(id: $registerId, projectId: $projectId) {
      id
      name
      identifierPrefix

      items {
        id
        name
        status
        identifier
        fullIdentifier
      }
    }
  }
`

export const GET_REGISTER_ITEMS_PUBLIC = gql`
  query registerItemsPublic_UI($subcontractorId: String!, $projectId: String!) {
    registerItems_public(
      subcontractorId: $subcontractorId
      projectId: $projectId
    ) {
      id
      name
      status
      createdAt
      register {
        id
        name
      }
      registerItemType {
        id
        name
      }
      projects {
        id
        name
      }
    }
  }
`

export const GET_WORKER_REGISTER_ITEM = gql`
  query workerRegisterItem_UI($registerId: String!, $registerItemId: String!) {
    registerItem_worker(id: $registerItemId, registerId: $registerId) {
      id
      name
      status
      identifier
      fullIdentifier

      register {
        typeLibraryTemplateId
      }

      typeTemplateResponse {
        id
        updatedAt
        responseData
      }

      templateResponse {
        id
        updatedAt
        responseData
      }

      checklistTemplates {
        id
        templateName
      }
    }
  }
`

export const GET_REGISTER_ITEM_PUBLIC = gql`
  query registerItemPublic_UI($id: String!, $subcontractorId: String!) {
    registerItem_public(id: $id, subcontractorId: $subcontractorId) {
      id
      name
      status

      companyRegisterItem {
        id
        name
        typeTemplateResponse
      }

      register {
        id
        name
      }

      registerItemType {
        id
        name
      }

      checklistTemplates {
        id
        templateName
        templateData
      }

      typeTemplateResponse
      templateResponse
      uploadTemplateResponse
    }
  }
`

export const GET_REGISTER_PUBLIC = gql`
  query registerPublic_UI($id: String!, $subcontractorId: String!) {
    register_public(id: $id, subcontractorId: $subcontractorId) {
      id
      name

      template {
        templateData
      }

      typeLibraryTemplate {
        templateData
      }

      settings {
        canAssignItemsToProject
        canAssignChecklists
        hideFollowUpDate
      }
    }
  }
`

export const GET_REGISTERS_LIST_PUBLIC = gql`
  query registersPublic_UI(
    $subcontractorId: String!
    $builderId: String
    $projectId: String
  ) {
    registers_public(
      subcontractorId: $subcontractorId
      builderId: $builderId
      projectId: $projectId
    ) {
      id
      name
    }
  }
`
export const CREATE_REGISTER_ITEM_PUBLIC = gql`
  mutation createRegisterItemPublic_UI(
    $subcontractorId: String!
    $input: CreateRegisterItemPublicInput!
  ) {
    createRegisterItem_public(
      subcontractorId: $subcontractorId
      input: $input
    ) {
      id
    }
  }
`

export const ARCHIVE_REGISTER_ITEM_PUBLIC = gql`
  mutation archiveRegisterItemPublic_UI(
    $id: String!
    $subcontractorId: String!
  ) {
    archiveRegisterItem_public(id: $id, subcontractorId: $subcontractorId) {
      id
    }
  }
`
